import type { AudioFramePayload, CanonicalKey } from "./aiHub/types";
import type { BarRhythmMapping } from "./aiHub/rhythmEngineCore";
import {
  OFF_COLOR,
  finishMapping,
  groupKeysByColumn,
  hsvToRgb,
  mapColumnUpRhythmFrame,
  resolveColumnCount,
  scaleRgb,
} from "./aiHub/rhythmEngineCore";
import {
  clamp01,
  createRhythmTransientState,
  probeRhythmTransient,
  rhythmMusicActive,
} from "./aiHub/rhythmAudio";
import type { RhythmMode } from "./audioVisualizerTypes";
import { normalizeRhythmMode } from "./rhythmModeOptions";
import {
  buildRhythmLayoutTopology,
  bottomRowIndex,
  columnLevelsToArray,
  computeLayoutColumnLevels,
  isClassicBufferCol,
  waterfallBandIndexForCol,
  type RhythmLayoutTopology,
} from "./rhythmLayoutTopology";

type Rgb = { r: number; g: number; b: number };

const PURE_BLACK: Rgb = { r: 0, g: 0, b: 0 };

const fract = (x: number) => x - Math.floor(x);
const hash01 = (n: number) => fract(Math.sin(n) * 43758.5453123);
const keyHash = (code: string) => {
  let h = 2166136261;
  for (let i = 0; i < code.length; i += 1) {
    h ^= code.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

function findCenterKey(
  keys: CanonicalKey[],
  preferCode = "KeyI"
): CanonicalKey {
  const preferred = keys.find((k) => k.uuid === preferCode && !k.isGap);
  if (preferred) return preferred;
  const active = keys.filter((k) => !k.isGap);
  if (active.length === 0) return keys[0];
  const avgRow = active.reduce((s, k) => s + k.row, 0) / active.length;
  const avgCol = active.reduce((s, k) => s + k.col, 0) / active.length;
  return active.reduce((best, k) => {
    const d = (k.row - avgRow) ** 2 + (k.col - avgCol) ** 2;
    const bd = (best.row - avgRow) ** 2 + (best.col - avgCol) ** 2;
    return d < bd ? k : best;
  }, active[0]);
}

function maxCoreDistanceFromCenter(
  keys: CanonicalKey[],
  center: CanonicalKey,
  topology: RhythmLayoutTopology
) {
  const mainColSet = new Set(topology.mainCols);
  let maxDist = 1;
  for (const key of keys) {
    if (key.isGap) continue;
    if (!mainColSet.has(key.col)) continue;
    const dx = key.col - center.col;
    const dy = key.row - center.row;
    const d = Math.sqrt(dx * dx + (dy / 0.82) ** 2);
    maxDist = Math.max(maxDist, d);
  }
  return maxDist;
}

// ===== 共鸣光域：圆心向外；左右各 4 列纯黑缓冲 =====
const classicState = {
  pulseEma: 0,
  pulseBaseEma: 0,
  lastPulse: 0,
  lastBlinkAt: 0,
  blinkOffUntil: 0,
  blinkOnUntil: 0,
};

function mapClassicResonanceFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const center = findCenterKey(keys, "KeyI");
  const maxCoreDist = maxCoreDistanceFromCenter(keys, center, topology);

  const bandPulse = clamp01(
    frame.bass * 0.45 + frame.mids * 0.35 + frame.treble * 0.25
  );
  const huePhase =
    (frame.timestamp / 5.5 +
      frame.bass * 55 +
      frame.mids * 85 +
      frame.treble * 110) %
    360;
  const breath =
    0.5 + 0.5 * Math.sin((frame.timestamp / 1000) * Math.PI * 2 * 0.55);
  const pulse = clamp01(0.55 * bandPulse + 0.45 * frame.rms);
  const now = frame.timestamp;

  classicState.pulseEma += (pulse - classicState.pulseEma) * 0.06;
  classicState.pulseBaseEma += (bandPulse - classicState.pulseBaseEma) * 0.025;
  const transient = Math.max(0, bandPulse - classicState.pulseBaseEma);
  const dp = pulse - classicState.lastPulse;
  classicState.lastPulse = pulse;

  const isRise =
    (transient > 0.04 && bandPulse > 0.16) ||
    (dp > 0.06 && pulse > 0.18) ||
    (pulse > classicState.pulseEma + 0.08 && pulse > 0.22);
  if (isRise && now - classicState.lastBlinkAt > 90) {
    classicState.lastBlinkAt = now;
    classicState.blinkOffUntil = now + 70;
    classicState.blinkOnUntil = classicState.blinkOffUntil + 170;
  }

  const radius =
    maxCoreDist *
    (0.52 + 0.48 * breath) *
    (0.5 + 0.95 * pulse) *
    (1 + (frame.beat ? 0.12 : 0));

  for (const key of keys) {
    if (key.isGap) continue;

    if (isClassicBufferCol(key.col, topology)) {
      keyColors[key.uuid] = PURE_BLACK;
      continue;
    }

    const dx = key.col - center.col;
    const dy = (key.row - center.row) / 0.82;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > radius) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }

    const ringIdx = Math.floor(d / 0.95);
    const ringMax = Math.max(1, Math.floor(radius / 0.95));
    const ringProgress = ringIdx / ringMax;
    const bandIdx = topology.colToBandIndex.get(key.col) ?? 0;
    const h =
      (huePhase +
        ringIdx * 35 +
        (bandIdx / Math.max(1, topology.bandCount - 1)) * 12) %
      360;
    let v = clamp01((1 - ringProgress * 0.14) * 0.98);

    if (now < classicState.blinkOffUntil) v = 0;
    else if (now < classicState.blinkOnUntil) v = clamp01(v * 1.55);

    if (v <= 0.02) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }
    keyColors[key.uuid] = hsvToRgb(h, 1, v);
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

// ===== 曲率频阶：对齐 ai-hub 频谱光柱（columnUp）+ ai-electron enrichFrame 频谱标尺 =====

/**
 * 频谱光柱列能量：按物理列等比切分 32-bin 全频谱，peak+avg 混合。
 * 避免 computeColumnEnergies 首列仅取 bin0（低音过弱、几乎无律动）。
 */
function computeColumnUpLevels(
  frame: AudioFramePayload,
  columnCount: number
): number[] {
  const n = frame.spectrum.length;
  if (n <= 0 || columnCount <= 0) return [];

  const beatBoost = frame.beat ? 0.18 : 0;
  const levels: number[] = [];

  for (let col = 0; col < columnCount; col += 1) {
    const start = Math.floor((col / columnCount) * n);
    const end = Math.max(start + 1, Math.ceil(((col + 1) / columnCount) * n));
    let peak = 0;
    let sum = 0;
    for (let i = start; i < end; i += 1) {
      const value = frame.spectrum[i] ?? 0;
      sum += value;
      peak = Math.max(peak, value);
    }
    const avg = sum / Math.max(1, end - start);
    const mixed = avg * 0.45 + peak * 0.55;
    levels.push(clamp01(mixed * (0.85 + frame.rms * 0.35) + beatBoost));
  }
  return levels;
}

function mapCurvatureFreqFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const columnCount = resolveColumnCount(keys);
  const columnLevels = computeColumnUpLevels(frame, columnCount);
  return mapColumnUpRhythmFrame(frame, keys, columnCount, columnLevels);
}

// ===== 频谱瀑布：从小键盘 Enter/. 向右列阶梯展开，自下而上 =====
const waterfallColEma = new Map<number, number>();

function mapSpectrumWaterfallFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const byCol = groupKeysByColumn(keys);
  const huePhase = (frame.timestamp / 48) % 360;
  const maxSpread = Math.max(1, topology.bandCount - 1);

  for (const col of topology.orderedCols) {
    const colKeys = byCol.get(col);
    if (!colKeys?.length) continue;

    const spreadIdx = waterfallBandIndexForCol(col, topology);
    const sourceCol = topology.orderedCols[spreadIdx] ?? col;
    const target = columnLevels.get(sourceCol) ?? 0;
    const prev = waterfallColEma.get(col) ?? 0;
    const smoothed = prev + (target - prev) * 0.24;
    waterfallColEma.set(col, smoothed);

    const sorted = colKeys.slice().sort((a, b) => b.row - a.row);
    const stairBoost = spreadIdx / maxSpread;
    const height = clamp01(smoothed * (0.55 + stairBoost * 0.55));
    const activeCount =
      height < 0.03 ? 0 : Math.max(1, Math.round(height * sorted.length));

    sorted.forEach((key, index) => {
      const stairCut = Math.max(0, index - Math.floor(stairBoost * 1.6));
      if (stairCut >= activeCount) {
        keyColors[key.uuid] = OFF_COLOR;
        return;
      }
      const isPeak = index === activeCount - 1;
      const v = isPeak ? 1 : 0.72 + height * 0.22;
      const h =
        (huePhase + spreadIdx * 22 + bottomRowIndex(key, topology) * 10) % 360;
      keyColors[key.uuid] = hsvToRgb(h, 0.92, v);
    });
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

// ===== 分频脉冲 =====
const splitPulseHuePhase = { value: 0 };

function mapSplitFreqPulseFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const rhythmRows = new Set(
    topology.orderedRows.slice(0, Math.min(3, topology.orderedRows.length))
  );
  const bodyRows = topology.orderedRows.filter((row) => !rhythmRows.has(row));
  const bodyThreshold = clamp01(frame.bass * 0.9 + frame.rms * 0.35);
  const fillRows = Math.round(bodyThreshold * Math.max(1, bodyRows.length));
  const pulseGate = frame.beat
    ? 1
    : clamp01(frame.mids * 0.7 + frame.treble * 0.5);
  const pulseBright = clamp01(0.25 + frame.rms * 0.85 + pulseGate * 0.45);

  splitPulseHuePhase.value =
    (splitPulseHuePhase.value + 1.8 + frame.treble * 5 + frame.mids * 3) % 360;

  for (const key of keys) {
    if (key.isGap) continue;
    const bandIdx = topology.colToBandIndex.get(key.col) ?? 0;

    if (rhythmRows.has(key.row)) {
      const toggle =
        0.5 + 0.5 * Math.sin(frame.timestamp / 140 + keyHash(key.uuid) * 0.01);
      const on = toggle > 0.38 && pulseBright > 0.2;
      if (!on) {
        keyColors[key.uuid] = OFF_COLOR;
        continue;
      }
      const hue =
        (splitPulseHuePhase.value + (keyHash(key.uuid) % 360) + key.row * 40) %
        360;
      const v = clamp01(pulseBright * (0.45 + toggle * 0.55));
      keyColors[key.uuid] = hsvToRgb(hue, 0.95, v);
      continue;
    }

    const fromBottom = bottomRowIndex(key, topology);
    if (fromBottom >= fillRows) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }

    const hue =
      (splitPulseHuePhase.value +
        (keyHash(key.uuid) % 280) +
        (bandIdx / Math.max(1, topology.bandCount - 1)) * 60) %
      360;
    const rowFade = 0.55 + (fromBottom / Math.max(1, fillRows)) * 0.45;
    const v = clamp01(bodyThreshold * rowFade);
    keyColors[key.uuid] = v > 0.04 ? hsvToRgb(hue, 0.92, v) : OFF_COLOR;
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

// ===== 极限爆律 =====
const ultraState = {
  transient: createRhythmTransientState(),
  flash: 0,
  flashHue: 0,
  litMask: new Map<string, boolean>(),
  generation: 0,
};

function mapUltraBeatStrobeFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const { hit } = probeRhythmTransient(
    frame,
    ultraState.transient,
    frame.timestamp
  );

  if (hit) {
    ultraState.generation += 1;
    ultraState.flash = clamp01(0.88 + frame.bass * 0.55 + frame.rms * 0.4);
    ultraState.flashHue =
      (ultraState.flashHue + 37 + hash01(ultraState.generation * 1.7) * 120) %
      360;
    ultraState.litMask.clear();
    for (const key of keys) {
      if (key.isGap) continue;
      const pick = hash01(keyHash(key.uuid) + ultraState.generation * 13.7);
      ultraState.litMask.set(key.uuid, pick > 0.38);
    }
  }

  ultraState.flash *= 0.82;
  if (ultraState.flash < 0.02) ultraState.flash = 0;

  const base = hsvToRgb(ultraState.flashHue, 0.92, 1);

  for (const key of keys) {
    if (key.isGap) continue;
    const lit = ultraState.litMask.get(key.uuid) ?? false;
    if (!lit || ultraState.flash < 0.03) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }
    keyColors[key.uuid] = scaleRgb(base, clamp01(ultraState.flash));
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

// ===== 波纹涟漪 =====
const RIPPLE_ROW_HUES = [48, 95, 175, 215, 285, 320];

function isRippleFixedKey(rowIndex: number, colIndex: number, code: string) {
  if ((colIndex * 3 + rowIndex * 5) % 7 < 3) return true;
  return hash01(keyHash(code) * 0.017) > 0.55;
}

function mapRippleFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const colSpan = Math.max(1, topology.orderedCols.length - 1);

  for (const key of keys) {
    if (key.isGap) continue;
    const colIndex = topology.colToBandIndex.get(key.col) ?? 0;
    const rowIndex = topology.rowToIndex.get(key.row) ?? 0;

    if (!isRippleFixedKey(rowIndex, colIndex, key.uuid)) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }

    const rowHueIdx = Math.min(RIPPLE_ROW_HUES.length - 1, rowIndex);
    const colT = colIndex / colSpan;
    const darken = clamp01(1 - colT * 0.88);
    const breath =
      0.32 +
      0.68 *
        (0.5 +
          0.5 *
            Math.sin(
              frame.timestamp / 420 +
                keyHash(key.uuid) * 0.0025 +
                colIndex * 0.35
            ));
    const amp = clamp01(0.28 + frame.rms * 0.72 + frame.mids * 0.2);
    const v = clamp01(darken * breath * amp);
    if (v < 0.04) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }
    const h =
      (RIPPLE_ROW_HUES[rowHueIdx]! + colT * 18 + frame.timestamp / 80) % 360;
    keyColors[key.uuid] = hsvToRgb(h, 0.9, v);
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

// ===== 分组闪断：按实际列序 (0,1)(2,3)… =====
const syncState = {
  transient: createRhythmTransientState(),
  activeColInPair: 0,
  burst: 0,
  hue: 0,
  blinkOffUntil: 0,
};

function mapSyncStrobeFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const { hit, bassZ } = probeRhythmTransient(
    frame,
    syncState.transient,
    frame.timestamp
  );
  const now = frame.timestamp;

  if (hit) {
    syncState.activeColInPair = syncState.activeColInPair === 0 ? 1 : 0;
    syncState.hue = (syncState.hue + 42 + hash01(now * 0.03) * 80) % 360;
    syncState.burst = clamp01(0.7 + frame.bass * 0.5 + frame.rms * 0.35);
    syncState.blinkOffUntil = now + 45;
  }

  syncState.burst *= 0.84;
  if (syncState.burst < 0.02) syncState.burst = 0;

  const gate = now < syncState.blinkOffUntil ? 0 : 1;
  const intensity = clamp01(
    gate *
      (0.35 +
        syncState.burst * 0.95 +
        frame.rms * 0.45 +
        (bassZ > 0.5 ? 0.12 : 0))
  );
  const base = hsvToRgb(syncState.hue, 0.95, 1);

  for (const key of keys) {
    if (key.isGap) continue;
    const colIdx = topology.colToBandIndex.get(key.col) ?? 0;
    const colInPair = colIdx % 2;
    if (colInPair !== syncState.activeColInPair || intensity < 0.04) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }
    keyColors[key.uuid] = scaleRgb(base, intensity);
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

// ===== 风暴漩涡：按实际布局固定区域 =====
const vortexState = {
  phase: 0,
  transient: createRhythmTransientState(),
};

function isVortexRegionKey(
  key: CanonicalKey,
  center: CanonicalKey,
  topology: RhythmLayoutTopology
) {
  const dx = key.col - center.col;
  const dy = key.row - center.row;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d <= 4.2) return true;
  const rightCols = topology.orderedCols.slice(
    Math.max(0, topology.orderedCols.length - 4)
  );
  if (rightCols.includes(key.col) && d <= 5.8) return true;
  return false;
}

function mapVortexStormFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[]
): BarRhythmMapping {
  const topology = buildRhythmLayoutTopology(keys);
  const columnLevels = computeLayoutColumnLevels(frame, topology);
  const keyColors: Record<string, Rgb> = {};
  const center = findCenterKey(keys, "KeyI");
  const { hit } = probeRhythmTransient(
    frame,
    vortexState.transient,
    frame.timestamp
  );

  const speed =
    0.12 + frame.rms * 0.75 + frame.treble * 0.35 + (hit ? 0.25 : 0);
  vortexState.phase = (vortexState.phase + speed) % (Math.PI * 2);
  const pulse = clamp01(
    0.5 * frame.bass + 0.25 * frame.treble + 0.35 * frame.rms
  );
  const armWidth = 0.45 + pulse * 1.8;
  const hueBase = (vortexState.phase / (Math.PI * 2)) * 360;

  for (const key of keys) {
    if (key.isGap) continue;
    if (!isVortexRegionKey(key, center, topology)) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }

    const dx = key.col - center.col;
    const dy = key.row - center.row;
    const ang = Math.atan2(dy, dx);
    let dAng = Math.abs(ang - vortexState.phase);
    if (dAng > Math.PI) dAng = Math.PI * 2 - dAng;

    const arm = Math.max(0, 1 - dAng / (0.28 + armWidth * 0.22));
    const r = Math.sqrt(dx * dx + dy * dy);
    const swirl = Math.max(0, 1 - r / 4.5);
    const rings = 0.5 + 0.5 * Math.sin(r * 2.2 - vortexState.phase * 1.8);
    const intensity = clamp01(
      Math.pow(arm, 0.55) * (0.3 + frame.rms * 0.95) +
        swirl * 0.22 * rings +
        (hit ? 0.18 : 0)
    );

    if (intensity < 0.05) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }
    const hue = (hueBase + r * 38 + key.col * 11) % 360;
    keyColors[key.uuid] = hsvToRgb(hue, 0.92, Math.max(0.22, intensity));
  }

  return finishMapping(
    keys,
    keyColors,
    columnLevelsToArray(topology, columnLevels)
  );
}

/** 静音时清零各模式持久状态，避免 EMA / 相位残留继续驱动灯效 */
export function resetBuiltinRhythmModeState(): void {
  classicState.pulseEma = 0;
  classicState.pulseBaseEma = 0;
  classicState.lastPulse = 0;
  classicState.lastBlinkAt = 0;
  classicState.blinkOffUntil = 0;
  classicState.blinkOnUntil = 0;

  waterfallColEma.clear();

  splitPulseHuePhase.value = 0;

  ultraState.flash = 0;
  ultraState.flashHue = 0;
  ultraState.generation = 0;
  ultraState.litMask.clear();
  Object.assign(ultraState.transient, createRhythmTransientState());

  syncState.activeColInPair = 0;
  syncState.burst = 0;
  syncState.hue = 0;
  syncState.blinkOffUntil = 0;
  Object.assign(syncState.transient, createRhythmTransientState());

  vortexState.phase = 0;
  Object.assign(vortexState.transient, createRhythmTransientState());
}

function mapAllKeysOff(keys: CanonicalKey[]): BarRhythmMapping {
  const keyColors: Record<string, Rgb> = {};
  for (const key of keys) {
    if (key.isGap) continue;
    keyColors[key.uuid] = OFF_COLOR;
  }
  return finishMapping(keys, keyColors, []);
}

export function mapBuiltinRhythmFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[],
  mode: RhythmMode | string
): BarRhythmMapping {
  if (!rhythmMusicActive(frame)) {
    return mapAllKeysOff(keys);
  }

  switch (normalizeRhythmMode(mode)) {
    case "curvatureFreq":
      return mapCurvatureFreqFrame(frame, keys);
    case "spectrumWaterfall":
      return mapSpectrumWaterfallFrame(frame, keys);
    case "splitFreqPulse":
      return mapSplitFreqPulseFrame(frame, keys);
    case "ultraBeatStrobe":
      return mapUltraBeatStrobeFrame(frame, keys);
    case "ripple":
      return mapRippleFrame(frame, keys);
    case "syncStrobe":
      return mapSyncStrobeFrame(frame, keys);
    case "vortexStorm":
      return mapVortexStormFrame(frame, keys);
    case "classic":
    default:
      return mapClassicResonanceFrame(frame, keys);
  }
}
